// src/pages/CreateSitePage.js - FIXED: Remove use_custom_colors field
import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { useNotifications } from "../contexts/NotificationContext";
import { apiService } from "../services/apiService";
import ColorPicker from "../components/ColorPicker";

export default function CreateSitePage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [form, setForm] = useState({
    niche: "",
    service: "",
    city: "",
    zipcode: "",
    state_name: "",
    state_code: "",
    phone: "",
    email: "",
    favicon_url: "",
    related_services: "",
    search_console_code: "",
    platform: "",
    account_id: "",
    primary_color: "#3B82F6",      // Default blue
    secondary_color: "#1F2937",     // Default gray
    accent_color: "#10B981",       // Default green
    // REMOVED: use_custom_colors field - not in database schema
  });
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState({});
  const [customColorsEnabled, setCustomColorsEnabled] = useState(false); // Local UI state only
  
  // Use refs to avoid recreation of functions
  const validationRef = useRef(validation);
  validationRef.current = validation;
  
  const navigate = useNavigate();
  const { success, error, loading: showLoading, updateNotification } = useNotifications();

  const totalSteps = 6;

  // Step definitions - STATIC
  const steps = [
  { id: 1, title: "Service Details", description: "Tell us about your business", icon: "🏢" },
  { id: 2, title: "Location Info", description: "Where is your business located?", icon: "📍" },
  { id: 3, title: "Contact Details", description: "How customers can reach you", icon: "📞" },
  { id: 4, title: "Design Options", description: "Customize colors (Optional)", icon: "🎨" },
  { id: 5, title: "Platform Setup", description: "Choose your deployment platform", icon: "🚀" },
  { id: 6, title: "Review & Generate", description: "Confirm details and create your site", icon: "✨" }
];

  // STABLE handleChange - NO dependencies that change
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    console.log('Input change:', name, value);
    
    const finalValue = name === 'state_code' ? value.toUpperCase() : value;
    
    setForm(prev => ({ ...prev, [name]: finalValue }));
    
    // Clear validation error using ref to avoid dependency
    if (validationRef.current[name]) {
      setValidation(prev => ({ ...prev, [name]: "" }));
    }
  }, []); // NO DEPENDENCIES - this function never changes

  // Fetch accounts when platform changes
  useEffect(() => {
    async function fetchAccounts() {
      if (!form.platform) {
        setAccounts([]);
        return;
      }
      
      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;
      if (!user) return;
      
      let data = [];
      if (form.platform === "netlify") {
        const { data: net } = await supabase
          .from("netlify_tokens")
          .select("id, token_name")
          .eq("user_id", user.id);
        data = net || [];
      } else if (form.platform === "aws") {
        const { data: aws } = await supabase
          .from("aws_credentials")
          .select("id, cred_name")
          .eq("user_id", user.id);
        data = aws || [];
      }
      setAccounts(data);
      setForm(prev => ({ ...prev, account_id: "" }));
    }
    
    fetchAccounts();
  }, [form.platform]);

  // Validation function - STABLE
  const validateStep = (step, formData) => {
  const errors = {};
  
  switch (step) {
    case 1:
      if (!formData.niche.trim()) errors.niche = "Niche is required";
      if (!formData.service.trim()) errors.service = "Service is required";
      if (!formData.related_services.trim()) errors.related_services = "Related services are required";
      break;
    case 2:
      if (!formData.city.trim()) errors.city = "City is required";
      if (!formData.zipcode.trim()) errors.zipcode = "Zipcode is required";
      if (!formData.state_name.trim()) errors.state_name = "State name is required";
      if (!formData.state_code.trim()) {
        errors.state_code = "State code is required";
      } else if (!/^[A-Z]{2}$/.test(formData.state_code)) {
        errors.state_code = "State code must be exactly two UPPERCASE letters (e.g. TX, AZ)";
      }
      break;
    case 3:
      if (!formData.phone.trim()) errors.phone = "Phone number is required";
      if (!formData.email.trim()) {
        errors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        errors.email = "Please enter a valid email address";
      }
      break;
    case 4:
      // Color validation (only if custom colors are enabled)
      if (customColorsEnabled) {
        if (!formData.primary_color || !formData.primary_color.match(/^#[0-9A-F]{6}$/i)) {
          errors.primary_color = "Valid primary color is required";
        }
        if (!formData.accent_color || !formData.accent_color.match(/^#[0-9A-F]{6}$/i)) {
          errors.accent_color = "Valid accent color is required";
        }
      }
      break;
    case 5:
      if (!formData.platform) errors.platform = "Platform selection is required";
      if (!formData.account_id) errors.account_id = "Account selection is required";
      break;
  }
  
  setValidation(errors);
  return Object.keys(errors).length === 0;
};

  const handleNext = useCallback(() => {
    if (validateStep(currentStep, form)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
    }
  }, [currentStep, form, validateStep, totalSteps]);

  const handlePrev = useCallback(() => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!validateStep(5, form)) return; // Validate step 5 (platform setup)
    
    setLoading(true);
    const loadingId = showLoading("Creating your site with AI content...");
    
    try {
      const relatedServicesArray = form.related_services
        .split("\n")
        .map(s => s.trim())
        .filter(s => s.length > 0);

      const { data: userData } = await supabase.auth.getUser();
      const user = userData?.user;
      if (!user) {
        throw new Error("User not found. Please login again.");
      }

      // FIXED: Only send fields that exist in database schema
      const siteData = {
        user_id: user.id,
        status: "working",
        niche: form.niche,
        service: form.service,
        city: form.city,
        zipcode: form.zipcode,
        state_name: form.state_name,
        state_code: form.state_code,
        phone: form.phone,
        email: form.email,
        favicon_url: form.favicon_url,
        related_services: relatedServicesArray,
        search_console_code: form.search_console_code,
        platform: form.platform,
        account_id: form.account_id,
        // Color fields - only include if custom colors are enabled
        ...(customColorsEnabled && {
          primary_color: form.primary_color,
          secondary_color: form.secondary_color,
          accent_color: form.accent_color
        })
      };

      const result = await apiService.createSite(siteData);
      
      if (result.success) {
        updateNotification(loadingId, {
          type: 'success',
          message: 'Site creation started! AI is generating your content...',
          duration: 5000
        });
        setTimeout(() => navigate("/dashboard"), 2000);
      } else {
        throw new Error(result.error || "Unknown error");
      }
    } catch (err) {
      updateNotification(loadingId, {
        type: 'error',
        message: 'Error: ' + err.message,
        duration: 7000
      });
    } finally {
      setLoading(false);
    }
  }, [form, validateStep, showLoading, updateNotification, navigate, customColorsEnabled]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="bg-white rounded-xl border border-gray-200 p-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            {steps.map((step, index) => (
              <div key={step.id} className="flex flex-col items-center relative">
                {index < steps.length - 1 && (
                  <div className={`absolute top-4 left-8 w-full h-0.5 ${
                    currentStep > step.id ? 'bg-indigo-600' : 'bg-gray-200'
                  }`} style={{ width: 'calc(100% + 2rem)' }} />
                )}
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold relative z-10 ${
                  currentStep > step.id 
                    ? 'bg-indigo-600 text-white' 
                    : currentStep === step.id
                      ? 'bg-indigo-100 text-indigo-600 ring-2 ring-indigo-600'
                      : 'bg-gray-200 text-gray-500'
                }`}>
                  {currentStep > step.id ? '✓' : step.id}
                </div>
                <div className="mt-2 text-center">
                  <p className={`text-xs font-medium ${
                    currentStep >= step.id ? 'text-gray-900' : 'text-gray-400'
                  }`}>
                    {step.title}
                  </p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {steps[currentStep - 1].icon} {steps[currentStep - 1].title}
            </h2>
            <p className="text-gray-600">{steps[currentStep - 1].description}</p>
          </div>
        </div>
        
        {/* Step Content */}
        <div className="mt-8">
          {currentStep === 1 && (
            <div className="space-y-6">
              {/* Business Niche */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Niche <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="niche"
                  value={form.niche}
                  onChange={handleChange}
                  placeholder="e.g. Home Services, Healthcare, Legal"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                    validation.niche ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {validation.niche && (
                  <p className="text-red-500 text-sm mt-1">{validation.niche}</p>
                )}
              </div>

              {/* Primary Service */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Primary Service <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="service"
                  value={form.service}
                  onChange={handleChange}
                  placeholder="e.g. Electrician, Plumber, Attorney"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                    validation.service ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {validation.service && (
                  <p className="text-red-500 text-sm mt-1">{validation.service}</p>
                )}
              </div>

              {/* Related Services */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Related Services <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="related_services"
                  value={form.related_services}
                  onChange={handleChange}
                  rows={4}
                  placeholder="List each service on a new line:&#10;Electrical Repairs&#10;Panel Upgrades&#10;Emergency Service&#10;New Installations"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                    validation.related_services ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                <p className="text-xs text-gray-500 mt-1">Each service on a new line (press Enter for each)</p>
                {validation.related_services && (
                  <p className="text-red-500 text-sm mt-1">{validation.related_services}</p>
                )}
              </div>
            </div>
          )}

          {currentStep === 2 && (
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                {/* City */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    placeholder="e.g. Phoenix"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                      validation.city ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {validation.city && (
                    <p className="text-red-500 text-sm mt-1">{validation.city}</p>
                  )}
                </div>

                {/* ZIP Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    ZIP Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="zipcode"
                    value={form.zipcode}
                    onChange={handleChange}
                    placeholder="e.g. 85001"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                      validation.zipcode ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {validation.zipcode && (
                    <p className="text-red-500 text-sm mt-1">{validation.zipcode}</p>
                  )}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {/* State Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="state_name"
                    value={form.state_name}
                    onChange={handleChange}
                    placeholder="e.g. Arizona"
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                      validation.state_name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {validation.state_name && (
                    <p className="text-red-500 text-sm mt-1">{validation.state_name}</p>
                  )}
                </div>

                {/* State Code */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    State Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="state_code"
                    value={form.state_code}
                    onChange={handleChange}
                    placeholder="AZ"
                    maxLength={2}
                    minLength={2}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                      validation.state_code ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                  />
                  {validation.state_code && (
                    <p className="text-red-500 text-sm mt-1">{validation.state_code}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <div className="space-y-6">
              {/* Business Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Phone <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="(555) 123-4567"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                    validation.phone ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {validation.phone && (
                  <p className="text-red-500 text-sm mt-1">{validation.phone}</p>
                )}
              </div>

              {/* Business Email */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="contact@yourbusiness.com"
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                    validation.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {validation.email && (
                  <p className="text-red-500 text-sm mt-1">{validation.email}</p>
                )}
              </div>

              {/* Favicon URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Favicon URL
                </label>
                <input
                  type="url"
                  name="favicon_url"
                  value={form.favicon_url}
                  onChange={handleChange}
                  placeholder="https://example.com/favicon.ico (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                />
              </div>

              {/* Google Search Console Code */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Google Search Console Code
                </label>
                <input
                  type="text"
                  name="search_console_code"
                  value={form.search_console_code}
                  onChange={handleChange}
                  placeholder="google-site-verification=... (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors"
                />
              </div>
            </div>
          )}

          {currentStep === 4 && (
            <div className="space-y-6">
              {/* Enable Custom Colors Toggle */}
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-blue-800 mb-1">🎨 Customize Site Colors</h4>
                    <p className="text-blue-700 text-sm">
                      Make your site unique with custom brand colors, or keep the professional blue theme.
                    </p>
                  </div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={customColorsEnabled}
                      onChange={(e) => {
                        setCustomColorsEnabled(e.target.checked);
                        // Reset to defaults if unchecked
                        if (!e.target.checked) {
                          setForm(prev => ({ 
                            ...prev, 
                            primary_color: "#3B82F6",
                            secondary_color: "#1F2937",
                            accent_color: "#10B981"
                          }));
                        }
                      }}
                      className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mr-2"
                    />
                    <span className="text-sm font-medium text-blue-800">Customize Colors</span>
                  </label>
                </div>
              </div>

              {/* Color Customization Options (only show if enabled) */}
              {customColorsEnabled ? (
                <div className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Primary Color */}
                    <ColorPicker
                      label="Primary Color (Headers, Buttons)"
                      value={form.primary_color}
                      onChange={(color) => handleChange({ target: { name: 'primary_color', value: color } })}
                    />

                    {/* Accent Color */}
                    <ColorPicker
                      label="Accent Color (Links, Highlights)"
                      value={form.accent_color}
                      onChange={(color) => handleChange({ target: { name: 'accent_color', value: color } })}
                      presetColors={['#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#06B6D4', '#F97316']}
                    />
                  </div>

                  {/* Live Preview */}
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="font-medium text-gray-800 mb-3">Preview</h4>
                    <div 
                      className="bg-white rounded-lg p-6 border shadow-sm"
                      style={{ 
                        borderColor: form.primary_color + '20'
                      }}
                    >
                      <h3 
                        className="text-xl font-bold mb-2"
                        style={{ color: form.primary_color }}
                      >
                        {form.service || 'Your Service'} in {form.city || 'Your City'}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Professional {(form.service || 'service').toLowerCase()} in {form.city || 'your city'}, {form.state_name || 'Your State'}.
                      </p>
                      <button 
                        className="px-4 py-2 rounded-lg text-white font-medium mr-3"
                        style={{ backgroundColor: form.primary_color }}
                      >
                        Contact Us
                      </button>
                      <span 
                        className="font-medium"
                        style={{ color: form.accent_color }}
                      >
                        Call {form.phone || '(555) 123-4567'}
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                /* Default Blue Theme Preview */
                <div className="space-y-6">
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h4 className="font-medium text-gray-800 mb-3">Default Professional Theme</h4>
                    <div className="bg-white rounded-lg p-6 border shadow-sm border-blue-100">
                      <h3 className="text-xl font-bold mb-2 text-blue-600">
                        {form.service || 'Your Service'} in {form.city || 'Your City'}
                      </h3>
                      <p className="text-gray-600 mb-4">
                        Professional {(form.service || 'service').toLowerCase()} in {form.city || 'your city'}, {form.state_name || 'Your State'}.
                      </p>
                      <button className="px-4 py-2 rounded-lg text-white font-medium mr-3 bg-blue-600">
                        Contact Us
                      </button>
                      <span className="font-medium text-emerald-500">
                        Call {form.phone || '(555) 123-4567'}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-3 text-center">
                      Professional blue theme - trusted and reliable
                    </p>
                  </div>
                </div>
              )}

              {/* Skip Option */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleNext}
                  className="text-gray-500 hover:text-gray-700 text-sm underline"
                >
                  Skip color customization and use default theme
                </button>
              </div>
            </div>
          )}

          {currentStep === 5 && (
            <div className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                {/* Netlify Option */}
                <div
                  onClick={() => handleChange({ target: { name: 'platform', value: 'netlify' } })}
                  className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${
                    form.platform === 'netlify'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-sm">N</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">Netlify</h3>
                        <p className="text-sm text-gray-500">Easy deployment</p>
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="platform"
                      value="netlify"
                      checked={form.platform === 'netlify'}
                      onChange={handleChange}
                      className="text-indigo-600"
                    />
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-green-600">
                      <span className="mr-2">✓</span> Instant deployments
                    </div>
                    <div className="flex items-center text-green-600">
                      <span className="mr-2">✓</span> Free SSL certificates
                    </div>
                    <div className="flex items-center text-green-600">
                      <span className="mr-2">✓</span> Global CDN included
                    </div>
                    <div className="flex items-center text-green-600">
                      <span className="mr-2">✓</span> Easy domain setup
                    </div>
                  </div>
                </div>

                {/* AWS Option */}
                <div
                  onClick={() => handleChange({ target: { name: 'platform', value: 'aws' } })}
                  className={`relative border-2 rounded-xl p-6 cursor-pointer transition-all ${
                    form.platform === 'aws'
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
                        <span className="text-white font-bold text-xs">AWS</span>
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">AWS S3</h3>
                        <p className="text-sm text-gray-500">Enterprise grade</p>
                      </div>
                    </div>
                    <input
                      type="radio"
                      name="platform"
                      value="aws"
                      checked={form.platform === 'aws'}
                      onChange={handleChange}
                      className="text-indigo-600"
                    />
                  </div>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center text-green-600">
                      <span className="mr-2">✓</span> 99.999% uptime SLA
                    </div>
                    <div className="flex items-center text-green-600">
                      <span className="mr-2">✓</span> CloudFront CDN optional
                    </div>
                    <div className="flex items-center text-green-600">
                      <span className="mr-2">✓</span> Custom domain support
                    </div>
                    <div className="flex items-center text-green-600">
                      <span className="mr-2">✓</span> Pay-as-you-scale
                    </div>
                  </div>
                </div>
              </div>
              
              {validation.platform && (
                <p className="text-red-500 text-sm">{validation.platform}</p>
              )}

              {/* Account Selection */}
              {form.platform && (
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select {form.platform === 'netlify' ? 'Netlify' : 'AWS'} Account *
                  </label>
                  {accounts.length === 0 ? (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-500 mb-4">
                        No {form.platform === 'netlify' ? 'Netlify tokens' : 'AWS credentials'} found
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate('/settings')}
                        className="text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        Add {form.platform === 'netlify' ? 'Netlify Token' : 'AWS Credentials'} in Settings
                      </button>
                    </div>
                  ) : (
                    <select
                      name="account_id"
                      value={form.account_id}
                      onChange={handleChange}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                        validation.account_id ? 'border-red-300 bg-red-50' : 'border-gray-300'
                      }`}
                    >
                      <option value="">Choose account</option>
                      {accounts.map(acc => (
                        <option key={acc.id} value={acc.id}>
                          {form.platform === "netlify" ? acc.token_name : acc.cred_name}
                        </option>
                      ))}
                    </select>
                  )}
                  {validation.account_id && (
                    <p className="text-red-500 text-sm mt-1">{validation.account_id}</p>
                  )}
                </div>
              )}
            </div>
          )}

          {currentStep === 6 && (
            <div className="space-y-6">
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="font-semibold text-lg mb-4">Site Preview</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Business Info</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-gray-500">Service:</span> {form.service}</p>
                      <p><span className="text-gray-500">Niche:</span> {form.niche}</p>
                      <p><span className="text-gray-500">Location:</span> {form.city}, {form.state_name}</p>
                      <p><span className="text-gray-500">Contact:</span> {form.phone}</p>
                    </div>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Deployment</h4>
                    <div className="space-y-2 text-sm">
                      <p><span className="text-gray-500">Platform:</span> {form.platform?.toUpperCase()}</p>
                      <p><span className="text-gray-500">Account:</span> {
                        accounts.find(acc => acc.id === form.account_id)?.[form.platform === 'netlify' ? 'token_name' : 'cred_name'] || 'Selected'
                      }</p>
                      {customColorsEnabled && (
                        <>
                          <p><span className="text-gray-500">Colors:</span> Custom theme</p>
                          <div className="flex space-x-2 mt-1">
                            <div className="w-4 h-4 rounded border" style={{ backgroundColor: form.primary_color }} title="Primary"></div>
                            <div className="w-4 h-4 rounded border" style={{ backgroundColor: form.accent_color }} title="Accent"></div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-6">
                <h3 className="font-semibold text-lg mb-2">🎯 What happens next?</h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <p>• AI will generate SEO-optimized content for your site</p>
                  <p>• Multiple pages will be created (home, about, contact, services)</p>
                  <p>• Professional design will be applied automatically</p>
                  {customColorsEnabled && <p>• Your custom colors will be applied to the design</p>}
                  <p>• Your site will be ready for deployment in 30-60 seconds</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handlePrev}
            disabled={currentStep === 1}
            className={`px-6 py-2 rounded-lg font-medium transition-colors ${
              currentStep === 1
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            ← Previous
          </button>

          <div className="text-sm text-gray-500">
            Step {currentStep} of {totalSteps}
          </div>

          {currentStep < totalSteps ? (
            <button
              type="button"
              onClick={handleNext}
              className="px-6 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
            >
              Next →
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="px-8 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Creating Site...</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span>Create Site</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}